<template>
    <ScreenLayout ref="screenLayout" :right-visible="false">
        <template slot="left">
            <div class="pa-3">
                <DateRangeList v-model="dateRange" @change="loadData" default="Last month"/>
                <v-checkbox :disabled="!admin" color="primary" v-model="currentUserOnly" label="Just my invoices"/>
                <v-select v-if="admin" v-model="category" :items="['Client Invoices', 'Processor Invoices', 'All Invoices']" label="Filter Invoices" />
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                    <v-btn :disabled="areAllInvoicesSelected" @click="selectAllInvoices" v-on="on" icon color="primary">
                        <v-icon>mdi-select</v-icon>
                    </v-btn>
                    </template>
                    <span>Select All Invoices</span>
                </v-tooltip>
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                    <v-btn :disabled="!areInvoicesSelected" @click="clearSelectedInvoices" v-on="on" icon color="primary">
                        <v-icon>mdi-select-off</v-icon>
                    </v-btn>
                    </template>
                    <span>Clear Selections</span>
                </v-tooltip>
                <SplitButton 
                    :text="pluralise('Send', this.selectedInvoiceKeys.length, 'Invoice', 'Invoices')" 
                    :disabled="!areInvoicesSelected" 
                    :menus="[{text: 'To Client'},{text: 'To Processor'},{text: 'To Both'},{text: 'Send Reminder'}, {text: 'To Bookkeeper'}]" 
                    class="full-width"
                    @click="emailSelectedInvoices"
                />
                <SplitButton 
                    menu-only
                    :text="pluralise('Mark', this.selectedInvoiceKeys.length, 'Invoice', 'Invoices')" 
                    :disabled="!areInvoicesSelected" 
                    :menus="[{text: 'Sent'},{text: 'Paid'},{text: 'Outstanding'}]" 
                    class="full-width"
                    @click="markInvoices"
                />
                <v-btn 
                    v-if = "admin" 
                    :disabled="!areInvoicesSelected" 
                    class="full-width"
                    @click="makeInsightBills"
                >Make Insight Bills</v-btn>
            </div>
            <SectionLayout :section="leftPageSection" />
        </template>
        
        <div v-for="(invoice, invoiceKey) in billingTableGrouped" @click="toggleInvoiceSelection(invoiceKey)" :key="invoiceKey" class="pa-3 elevation-1 ma-3 white-background invoice-hoverable" :class="{'blue-hilight': isInvoiceSelected(invoiceKey)}" style="overflow:auto;border:solid 1px transparent;max-width:700px;" :ref="'invoice-'+ invoiceKey">
 
                <div style="font-size:30px">INVOICE <span style="color:#bbb">{{getInvoiceId(invoice)}}</span></div> 
                <table>
                    <tr>
                        <td align="right" style="padding:3px"><b>To:</b></td>
                        <td>{{invoice[0].payer.firstName}} {{invoice[0].payer.lastName}} ({{invoice[0].payer.email}})</td>
                    </tr>
                    <tr>
                        <td align="right" style="padding:3px"><b>From:</b></td>
                        <td>{{invoice[0].recipient.firstName}} {{invoice[0].recipient.lastName}}</td>
                    </tr>
                    <tr>
                        <td align="right" style="padding:3px"><b>ABN:</b></td>
                        <td>{{getProcessorDetails(invoice[0].recipient.email, 'abn')}}</td>
                    </tr>
                    <tr>
                        <td align="right" style="padding:3px"><b>Account:</b></td>
                        <td>{{getProcessorDetails(invoice[0].recipient.email, 'account')}}</td>
                    </tr>
                </table>   

                <br>

                <table width="100%">
               
                    <tr v-for="charge in invoice">
                        <td>
                            {{charge.charge_date | date}}
                        </td>
                        <div @click.stop>

                        <v-menu offset-y>
                            <template v-slot:activator="{ on }">
                            <td v-on="on">
                            {{charge.client}}
                            </td>
                            </template>
                            <v-list>
                                <v-list-item @click="deleteCharge(charge)">
                                    <v-list-item-title>Delete Charge</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        </div>
                        
                        <td>
                            {{charge.comment}}
                        </td>
                        <td align="right">
                            {{charge.amount}} <strong v-if="charge.status">*** {{charge.status}} ***</strong>
                        </td>
                    </tr>
                </table>
            
                <table style="width:100%">
                    <tr>
                        <td style="text-align:right;padding:3px;width:99%">Total:</td>
                        <td style="text-align:right">{{invoiceTotal(invoice) | cents}}</td>
                    </tr>
                    <tr>
                        <td style="text-align:right;padding:3px;">+GST: </td>
                        <td style="text-align:right">{{(invoiceTotal(invoice) * 0.1) | cents}}</td>
                    </tr>
                    <tr>
                        <td style="text-align:right;padding:3px;">Amount Payable:</td>
                        <td style="text-align:right"><b>{{(invoiceTotal(invoice) * 1.1) | cents}}</b></td>
                    </tr>
                </table>
                <small>* Direct debit is on {{nextTwentieth | shortDate}}. Manual payments due by the 15th.</small>

                
            <!-- <SectionLayout v-for="section in page.sections" :section="section" :key="section.key"/> -->
            <!-- <ScreenTable :table="mergeSection(this.page.sections[0], this.page.collections[1].data)" /> -->
        </div>
        <!-- <div v-if="moreButtonVisible" @click="page.collections[0].limit += 100;loadData()" class="v-btn">Show More</div> -->


        <template slot="right">
 
                   
        </template>

    </ScreenLayout>
</template>

<script>
import {getDate, getMonth, getYear, format} from 'date-fns'
import LayoutContainer2 from '@/ui-elements/LayoutContainer2'
import SectionLayout from '@/screens/SectionLayout'
import ElementLayout from '@/screens/ElementLayout'
import LayoutReadOnly from '@/ui-elements/LayoutReadOnly'
import ElementMenu from '@/screens/ElementMenu'
import ScrollHelpers from '../helpers/ScrollHelpers'
import ScreenLayout from '../screens/ScreenLayout'
import PageLayout from '../screens/PageLayout'
import VueJsonToCsv from 'vue-json-to-csv'

import DateRangeList from '@/ui-elements/DateRangeList'
import ScreenData from './ScreenData'
import ScreenTable from './ScreenTable'
import Utilities from '../helpers/Utilities'
import StringHelpers from '../helpers/StringHelpers'
import DataHelpers from '../helpers/DataHelpers'
import LookupData from '../helpers/LookupData'
import TemplateHelpers from '../helpers/TemplateHelpers'
import ArrayHelpers from '../helpers/ArrayHelpers'
import AlertHelpers from '../helpers/AlertHelpers'
import MergeHelpers from '../helpers/MergeHelpers'
import EmailHelpers from '../helpers/EmailHelpers'
import SplitButton from '@/ui-elements/SplitButton'
import ContactHelpers from '../helpers/ContactHelpers'
import DateHelpers from '../helpers/DateHelpers'
import NiceData from '../data/NiceData'

class PageSection {
    constructor(section) { 
        this.section = section
    }
    fieldByName(name) {
        return this.section.fields.find(o => o.name == name) || null
    }
}



export default {
    data() {
        return {
            selectedInvoiceKeys: [],
            tables: {},
            billingTable: {},
            pageLoaded: false,
            page2: null,
            dateRange: {
                start: null,
                end: null,
                text: null
            },
            currentUserOnly: true,
            category: 'Client Invoices',

            leftPageSection: {
                    title: 'Parameters',
                    flat: true,
                    hideTitle: true,
                    hideIcons: true,
                    hideIndicators: true,
                    fields: [
                        {
                            name: 'date_range',
                            type: 'DateRange',
                            control: 'DateRange',
                            key: '111',
                            title: 'Date Range',
                            complete: true,
                            value: null,
                        },
                    ] 
            },

            page: {sections:[], collections:[]},

        
        }
    },
    components: {
        SectionLayout,
        ScreenLayout,
        ScreenTable,
        DateRangeList,
        VueJsonToCsv,
        SplitButton,
    },
    
    props: {
        admin: String,
    },

    computed: {

        filters() {
            return {
                u: this.currentUserOnly,
                c: this.category,
                d: this.dateRange,
            }
        },

        nextTwentieth() {
            let today = new Date()
            let day = getDate(today)
            let month = getMonth(today) + (day > 15 ? 1 : 0)
            let year = getYear(today)
            return year + '-' + month + '-20'
        },

        billingTableGrouped() {
            let obj = {}
            if(!this.billingTable.records)
                return obj

            const currentUserEmail = DataHelpers.getUserEmail()
            this.billingTable.records.forEach(billingRecord => {
                let allowForUser = (!this.currentUserOnly) || (currentUserEmail == billingRecord.recipient.email )
                
                let allowForCategory = true
                if(this.category == 'Processor Invoices')
                    allowForCategory = (billingRecord.recipient.email == 'billing@insightprocessing.com.au')
                else if(this.category == 'Client Invoices')
                    allowForCategory = (billingRecord.recipient.email != 'billing@insightprocessing.com.au')
                
                const payerExists = Boolean(billingRecord.payer) && Boolean(billingRecord.payer.email)
                
                if(payerExists && allowForUser && allowForCategory) {
                    //const invoiceKey = billingRecord.payer.email
                    const invoiceKey = billingRecord.payer.email + '-' + billingRecord.recipient.email
                    if(!obj[invoiceKey]) {
                        obj[invoiceKey] = []
                    }
                    obj[invoiceKey].push(billingRecord)
                } else {
                    //do nothing
                }
            })
            return obj
        },



        selectedInvoices() {
            let result = {}
            this.selectedInvoiceKeys.forEach(key => {
                if(!this.billingTableGrouped[key])
                    debugger
                result[key] = this.billingTableGrouped[key].slice()
            })
            return result
        },

        mergedSection() {
            try {
                return this.mergeSection(this.page.sections[0], this.page.collections[1].data)
            } catch (e) {
                //fails when page hasn't yet loaded
                return null
            }

        },
        dates() {
            return this.dateRange
        },

        moreButtonVisible() {
            if(!this.page.collections || !this.page.collections.length || !this.page.collections[1].data || !this.page.collections[1].data.records)
                return false
            return this.page.collections[1].data.records.length >= this.page.collections[1].limit
        },

        areInvoicesSelected() {
            return (this.selectedInvoiceKeys.length > 0)
        },
        areAllInvoicesSelected() {
            return (this.selectedInvoiceKeys.length == Object.keys(this.billingTableGrouped).length)
        },

    },

    methods: {

        getInvoiceId(invoice) {
            let firstInvoice = invoice[0]
            let payer = firstInvoice.payer
            let initials = ContactHelpers.initials(payer) + payer.lastName[payer.lastName.length -1].toUpperCase()
            
            let recipient = firstInvoice.recipient
            let recipientInitials = ContactHelpers.initials(recipient)

            let backwardsDate = firstInvoice.charge_date
            let dateObj = DateHelpers.backwardsToDate(backwardsDate)
            let monthYear = format(dateObj, 'MMyy')
            return initials + recipientInitials + '-' + monthYear
        },

        markInvoices(menu) {
            for(let invoiceKey in this.selectedInvoices) {
                const status = (menu.text == 'Outstanding' ? null : menu.text)
                
                console.log('Marking as ' + status + ' for ' + invoiceKey)
                const invoice = this.selectedInvoices[invoiceKey]
                invoice.forEach(charge => {
                    ScreenData.saveFieldValue('insight', 'billing', charge.key, 'status', status)
                })
            }
        },

        makeInsightBills() {
            for(let invoiceKey in this.selectedInvoices) {
                const dt = DateHelpers.todayString()
                const invoice = this.selectedInvoices[invoiceKey]
                const total = this.invoiceTotal(invoice)
                const processor = invoice[0].recipient
                const broker = invoice[0].payer
                const brokerParams = LookupData.processors().find(o => o.email == processor.email) || {rate: .25, email: 'unknown', firstName: 'Unknown'}
                const itemAmount = brokerParams.rate * total
                const percentString = StringHelpers.formatPercent(brokerParams.rate)
                const totalString = StringHelpers.formatCurrency(total)
                const note = `Comms on ${percentString} of ${totalString}`
                const recipient = {
                    firstName: 'Insight',
                    lastName: 'Processing',
                    email: 'billing@insightprocessing.com.au',
                }

                if(processor.email == 'billing@insightprocessing.com.au') {
                    AlertHelpers.showAlert("Can't generate from an existing Insight Invoice")
                    continue
                }
                
                let page = {
                    rowKey: DataHelpers.getKey(),
                    table: 'billing',
                    scope: 'insight',
                    elements: [
                        {fieldPos: 'i1', name: 'charge_date', value: dt},
                        {fieldPos: 'f1', name: 'amount', value: itemAmount},
                        {fieldPos: 'f2', name: 'note', value: note},
                        {fieldPos: 'i2', name: 'recipient', value: recipient},
                        {fieldPos: 'i3', name: 'payer', value: processor},
                        {fieldPos: 'i4', name: 'clients', value: ContactHelpers.fullName(broker)},
                    ]
                }
                ScreenData.savePageValues(page)
                AlertHelpers.showAlert('Insight Invoices Created')
            }
        },

        pluralise(prefix, count, singularVersion, pluralVersion) {
            let result = prefix + ' '
            if(count < 1)
                result += 'no'
            else
                result += count
            if(count == 1)
                result += ' ' + singularVersion
            else
                result += ' ' + pluralVersion
            return result
        },
        selectAllInvoices() {
            this.selectedInvoiceKeys = Object.keys(this.billingTableGrouped)
        },
        clearSelectedInvoices() {
            this.selectedInvoiceKeys = []
        },
        mergeSection(section, table) {

            if(!(section && section.fields && table && table.fields) )
                return {}

            let toSection = {}
            toSection.fields = []
            section.fields.forEach(field => {
                
                const tableField = table.fields.find(o => o.name == field.name)
                const mergedField = Object.assign({}, tableField, field)
                this.setDefaultProperties(mergedField)
                
                toSection.fields.push(mergedField)
            })

            toSection.records = []
            
            table.records.forEach(record => {
                let toRecord = {}
                toSection.fields.forEach(field => {
                    const value = record[field.name]
                    toRecord[field.name] = ScreenData.formatForDisplay(value, field.type)
                })
                toSection.records.push(toRecord)
            })


            return toSection

        },

        toggleInvoiceSelection(invoiceKey) {
            ArrayHelpers.toggleInArray(this.selectedInvoiceKeys, invoiceKey)
        },

        isInvoiceSelected(email) {
            return (this.selectedInvoiceKeys.indexOf(email) > -1)
        },

        invoiceTotal(invoice) {
            return invoice.reduce((a, b) => a + (b.amount || 0), 0)
        },

        getProcessorDetails(email, field) {
            let processors = LookupData.processors()
            let processor = processors.find(p => p.email == email)
            if(processor)
                return processor[field]
            else
                return ''
        },

        setDefaultProperties(field) {
            field.title = field.title || StringHelpers.snakeToNormal(field.name)
            field.align = field.align || (['Currency', 'Number'].includes(field.type) ? 'right' : null)
        },


        emailSelectedInvoices(menu) {

            if(menu && menu.text == 'To Bookkeeper') {
                this.emailAllInvoicesToBookkeeper()
                return
            }
                

            const templateName = (menu && menu.text == 'Send Reminder' ? 'Send Invoice Reminder': 'Send Invoice')
            TemplateHelpers.fetchTemplate('global', templateName).then(template => {
                console.log('Template is ', template)

                if(!template) {
                    AlertHelpers.showAlert('Template not found: Notify Checklist Updated')
                    return
                }
                let selectedInvoices = this.selectedInvoices
                for(let invoiceKey in selectedInvoices) {
                    const invoiceText = this.$refs['invoice-' + invoiceKey][0].outerHTML
                    const payerEmail = selectedInvoices[invoiceKey][0].payer.email
                    this.getBillingEmail(payerEmail).then(billingEmail => {

                        const recipentEmail = selectedInvoices[invoiceKey][0].recipient.email
                        let greeting = selectedInvoices[invoiceKey][0].payer.firstName
                        let to, cc
                        if(!menu || menu.text == 'To Both' || menu.text == 'Send Reminder') {
                            to = billingEmail
                            cc = recipentEmail
                        } else if(menu.text == 'To Client') {
                            to = billingEmail
                            cc = null
                        } else if(menu.text == 'To Processor') {
                            to = recipentEmail
                            cc = null
                            greeting = selectedInvoices[invoiceKey][0].recipient.firstName
                        }
                        this.emailInvoice(template, invoiceText, greeting, to, cc)
                    })
                }


            })
        },

        emailAllInvoicesToBookkeeper() {
            TemplateHelpers.fetchTemplate('global', 'Send Invoice').then(template => {
                console.log('Template is ', template)   
                let emailText = ''
                for(let invoiceKey in this.selectedInvoices) {
                    const invoiceText = this.$refs['invoice-' + invoiceKey][0].outerHTML
                    emailText += invoiceText + '<br/><br/>'
                }
                const to = 'accounting@insightprocessing.com.au'
                const cc = 'billing@insightprocessing.com.au'

                this.emailInvoice(template, emailText, 'Emma', to, cc)
            })         
        },

        getBillingEmail(email) {
            return new Promise( (resolve, reject) => {
                ContactHelpers.fetchContactByEmail(email).then(contact => {
                    
                    const uid = contact.meta.key
                    const tenantKey = DataHelpers.getTenantKey()
                    let ref = DataHelpers.getRootRef().child(`users/${uid}/offices/${tenantKey}/preferences/email/billing`)
                    NiceData.fetchValue(ref).then(billingEmail => {
                        
                        if(!billingEmail)
                            billingEmail = email
                        resolve(billingEmail)
                    })
                }).catch(err => {
                    console.log('getBillingEmail threw an error so just used normal email address', err)
                    resolve(email)
                })    
            })
        },

        emailInvoice(template, invoiceHtml, greeting, to, cc) {

            console.log('*** sending invoice ***', to, cc,)
 
            let customFields = {
                greeting, 
                sender: this.$store.getters.user.firstName,
                invoice: invoiceHtml,
            }


            let body = MergeHelpers.simpleMerge(template.body, customFields)

            let email = {}
            email.to = to
            email.cc = cc
            email.subject = template.subject || ''
            email.body = body


            email.bcc = DataHelpers.getUserEmail()


            EmailHelpers.sendEmail(email).then(msg => {
                if(msg != 'OK')
                    AlertHelpers.showAlert('Email Failed - ' + msg)
                else
                    AlertHelpers.showAlert('Invoice Sent to ' + greeting)
            })
            
         
        }, 

        async deleteCharge(charge) {
            let amountString = StringHelpers.formatCurrency(charge.amount)
            if(await this.$root.$confirm('Delete', `Are you sure you want to delete this charge for ${charge.client} amounting to ${amountString} from the invoice?`, { color: 'red' }) == true) {
                console.log('deleting charge', charge.key)
                ScreenData.deleteRecord('insight', 'billing', charge.key)
            }
        },

        loadData() {
            let collection = {}
            console.log('running load data')
            collection.scope = 'insight'
            collection.table = 'billing'
            collection.orderBy = 'charge_date'
            collection.startAt = this.dateRange.start
            collection.endAt = this.dateRange.end
            this.$set(collection, 'data', {})
            ScreenData.injectTable(this.billingTable, collection).then(result => {
                console.log(result, this.billingTable)
            })

        }

        

    },

    watch: {
        'filters': {
            deep: true, 
            handler: function(newVal) {
                this.clearSelectedInvoices()
            }
        }
    },




    mounted() {
           console.log('***REFS****', this.$refs)
    },

    created() {
        this.loadData()
    }

}
</script>

<style>
    .invoice-hoverable:hover {
        border: solid 1px steelblue!important;
    }
</style>




// WEBPACK FOOTER //
// BillingOld.vue?621d9e90